import type {
  ColoredMaterial,
  SalesOpportunity,
} from "@color-finder/color-finder-api/models";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { type Dispatch, type FC, useState } from "react";
import { useAppConfig } from "../../../config/app-config";
import { logCustomEvent } from "../../../datadog";
import { OpportunityAutocomplete } from "../../input/opportunityAutocomplete/OpportunityAutocomplete";

export interface DigitalSampleRequestDialogProps {
  open: boolean;
  onClose: Dispatch<void>;
  material: ColoredMaterial;
  userEmail?: string;
  onSubmit: Dispatch<{
    finish: string;
    comment?: string;
    requester?: string;
    opportunity: SalesOpportunity;
  }>;
}

const defaultFinish = "Glossy";

export const DigitalSampleRequestDialog: FC<
  DigitalSampleRequestDialogProps
> = ({ open, onClose, material, userEmail, onSubmit }) => {
  const [finish, setFinish] = useState(defaultFinish);
  const [finishValidationError, setFinishValidationError] = useState<string>();
  const [comment, setComment] = useState("");
  const [opportunity, setOpportunity] = useState<SalesOpportunity>();
  const [opportunityValidationError, setOpportunityValidationError] =
    useState<string>();
  const [config] = useAppConfig();

  const updateFinish = (finish: string) => {
    setFinish(finish);
    validateFinish(finish);
  };

  const validateFinish = (finish?: string): boolean => {
    if (!finish) {
      setFinishValidationError("This value is required.");
      return false;
    } else {
      setFinishValidationError(undefined);
      return true;
    }
  };

  const updateOpportunity = (opportunity: SalesOpportunity | undefined) => {
    setOpportunity(opportunity);
    validateOpportunity(opportunity);
  };

  const validateOpportunity = (opportunity?: SalesOpportunity): boolean => {
    if (config.salesOpportunities.enabled && !opportunity) {
      setOpportunityValidationError("This value is required.");
      return false;
    } else {
      setOpportunityValidationError(undefined);
      return true;
    }
  };

  const resetForm = () => {
    setFinish(defaultFinish);
    setOpportunity(undefined);
    setFinishValidationError(undefined);
    setOpportunityValidationError(undefined);
    setComment("");
  };

  const onFormSubmit = () => {
    if (validateFinish(finish) && validateOpportunity(opportunity)) {
      logCustomEvent("digital-sample-request-submit", {
        material_grade: material.material.shortGrade,
        elj_experiment_id: material.eljExperimentId,
        finish,
      });
      resetForm();
      onSubmit({
        finish,
        requester: userEmail,
        comment: comment.length > 0 ? comment : undefined,
        opportunity: opportunity!,
      });
    }
  };

  const onFormClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onFormClose}>
      <DialogTitle>Request creation of a Digital Sample</DialogTitle>
      <DialogContent>
        <DialogContentText mb={2}>
          To request a creation of a Digital Sample fill the form below. You
          will be contacted by a CDC employee as soon as your request is
          processed.
        </DialogContentText>
        <TextField
          margin={"dense"}
          label="Requester E-Mail"
          fullWidth
          required
          disabled
          value={userEmail}
        />
        <TextField
          margin={"dense"}
          label="Grade"
          fullWidth
          required
          disabled
          value={material.material.grade}
        />
        <TextField
          margin={"dense"}
          label="Covestro Color Code"
          fullWidth
          required
          disabled
          value={
            material.color.finalColorCode ?? material.color.preliminaryColorCode
          }
        />
        <TextField
          margin={"dense"}
          label="Digital Sample Finish"
          fullWidth
          required
          error={Boolean(finishValidationError)}
          helperText={finishValidationError}
          value={finish}
          onChange={(e) => updateFinish(e.target.value)}
        />
        {config.salesOpportunities.enabled && (
          <OpportunityAutocomplete
            value={opportunity}
            onChange={updateOpportunity}
            error={Boolean(opportunityValidationError)}
            margin={"dense"}
            helperText={opportunityValidationError}
            required
          />
        )}
        <TextField
          margin={"dense"}
          label="Additional Remarks"
          fullWidth
          multiline
          value={comment}
          rows={5}
          onChange={(e) => setComment(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onFormClose}>Cancel</Button>
        <Button
          disabled={
            finishValidationError !== undefined ||
            opportunityValidationError !== undefined
          }
          onClick={onFormSubmit}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
